import { skipToken, useSuspenseQueries } from '@tanstack/react-query';
import clsx from 'clsx';
import React from 'react';

import * as api from '@gitbook/api-client';
import { CONTENT_DEFAULT_BRANCH } from '@gitbook/hive-core';
import { PaletteButton } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Actions,
    Alert,
    Button,
    Description,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    Heading,
} from '@gitbook/spine-react';

import { apiQuery, useAssertCurrentOrganizationId } from '../../state';
import { useGetChangeRequestAgent } from '../Agent';
import { AgentChatInput } from '../AgentChat';
import { encodeDocumentForAPI, useBasicEditorPersistedValue } from '../BasicEditor';
import { ContentReferenceContextProvider } from '../ContentReferences';
import { getFirstSpaceInOrgAllContent } from '../Organization';
import { SpaceIcon, useSelectSpacePalette } from '../Space';
import { useCreateChangeRequest } from './useCreateChangeRequest';

type CreateAgentChangeRequestDialogProps = {
    /** Scope the dialog to a single space */
    spaceId?: string;
};

/**
 * Dialog to create a new change request with the GitBook Agent.
 *
 * In the future, we'll improve so that it doesn't require the user to pick a space
 * and instead plan its work accross multiple spaces.
 */
export function CreateAgentChangeRequestDialog(props: CreateAgentChangeRequestDialogProps) {
    return (
        <Dialog>
            <CreateAgentChangeRequestDialogContent {...props} />
        </Dialog>
    );
}

function CreateAgentChangeRequestDialogContent(props: CreateAgentChangeRequestDialogProps) {
    const { spaceId: forcedSpaceId } = props;

    const organizationId = useAssertCurrentOrganizationId();
    const [{ data: forcedSpace }, { data: organizationContent }] = useSuspenseQueries({
        queries: [
            apiQuery('getSpaceById', forcedSpaceId ? { spaceId: forcedSpaceId } : skipToken),
            apiQuery('getOrganizationAllContent', { organizationId }),
        ],
    });
    const firstEditableSpace = React.useMemo(() => {
        return getFirstSpaceInOrgAllContent(organizationContent)?.space || null;
    }, [organizationContent]);

    const [space, setSpace] = React.useState<api.Space | null>(forcedSpace ?? firstEditableSpace);
    const { value, setValue, resetValue } = useBasicEditorPersistedValue(
        'changeRequestAgentPrompt'
    );
    const createChangeRequest = useCreateChangeRequest();
    const getAgent = useGetChangeRequestAgent();

    const onSubmit = async () => {
        if (!space) {
            return;
        }

        await createChangeRequest({
            space,
            source: forcedSpaceId ? 'space-agent-button' : 'org-agent-button',
            navigateTo: {
                sideSheet: {
                    type: 'agent',
                },
            },
            onBeforeNavigate: async ({ changeRequest }) => {
                const agent = getAgent({
                    organizationId,
                    spaceId: changeRequest.space,
                    changeRequestId: changeRequest.id,
                });
                agent.postMessage({
                    role: api.AIMessageRole.User,
                    content: encodeDocumentForAPI(value.document),
                });
                resetValue();
            },
        });

        // TODO: fix close of dialog causing a react crash because of depth of state updates
        // closeDialog();
    };

    const spaceSelectionPalette = useSelectSpacePalette({
        selectedSpaceId: space?.id,
        organizationId,
        onSpaceSelected: setSpace,
    });

    return (
        <>
            <DialogHeader>
                <Heading>Implement a change request</Heading>
                <Description>
                    Describe the task of the change request, and let the GitBook Agent craft the
                    content under your guidance.
                </Description>
            </DialogHeader>
            <DialogBody>
                <div className="flex flex-col gap-3">
                    <ContentReferenceContextProvider
                        value={{
                            organization: organizationId,
                            ...(space
                                ? { space: space.id, hiveBranch: CONTENT_DEFAULT_BRANCH }
                                : {}),
                        }}
                    >
                        <AgentChatInput
                            value={value}
                            setValue={setValue}
                            placeholder="What do you want to do?"
                            className={clsx(
                                'rounded border',
                                value.isFocused ? 'border-primary' : 'border-base'
                            )}
                        />
                    </ContentReferenceContextProvider>
                    <Alert
                        icon={<Icon.AIAgentAnimated state="loading" className="mx-3 size-5" />}
                        size="xsmall"
                        details={
                            <>
                                <strong className="font-medium">
                                    What can the GitBook Agent do?
                                </strong>{' '}
                                Read through the docs, make edits, create new pages, etc. Almost
                                anything you can do yourself, the GitBook Agent can do for you.
                            </>
                        }
                    />
                </div>
            </DialogBody>
            <DialogFooter className={forcedSpaceId ? undefined : 'justify-between'}>
                {forcedSpaceId ? null : (
                    <PaletteButton
                        label={space?.title ?? 'Select space'}
                        kind="secondary"
                        leadingIcon={space ? <SpaceIcon space={space} /> : null}
                        trailingIcon={Icon.ChevronDown}
                        palette={spaceSelectionPalette}
                        tooltip={{
                            label: 'Select the space to create the change request in.',
                        }}
                    />
                )}
                <Actions>
                    <DialogCloseButton />
                    <Button
                        kind="primary"
                        label="Start change request"
                        leadingIcon={Icon.ChangeRequestNew}
                        disabled={!space}
                        onPress={async () => {
                            await onSubmit();
                        }}
                    />
                </Actions>
            </DialogFooter>
        </>
    );
}
